import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode,
} from 'react';
import {
  Box, Fab, ListItemIcon, ListItemText, Menu, MenuItem, Typography,
} from '@mui/material';

import Icon from '@/components/ui/Icon';
import FeedbackModal from './FeedbackModal';

/**
 * EIN FAB-System statt zweier schwebender Knoepfe (Punkt 6 der Aufnahme vom
 * 09.09.2026, Auftrag Alex 10.09.).
 *
 * ── DAS PROBLEM, DAS DIESE DATEI LOEST ───────────────────────────────────
 * Bis zum 10.09. gab es VIER schwebende Knoepfe: drei kontextbezogene
 * Plus-FABs (Case, Beratungsstrecke, Kundendetail — alle unten rechts) und
 * den Feedback-FAB mittig rechts, global auf jeder Seite. Auf einer
 * Detailseite standen damit zwei Knoepfe gleichzeitig im Bild.
 *
 * 🔥 **Ein Umzug des Feedbacks in den Plus-FAB allein haette es KAPUTT
 * gemacht** (Frage 6.2 der Aufnahme): den Plus-FAB gibt es nur auf drei
 * Detailseiten — aus Pipeline, Meetings und Cockpit waere das Melden
 * ersatzlos verschwunden, wo es heute ueberall geht.
 *
 * ── DIE AUFLOESUNG ───────────────────────────────────────────────────────
 * Der Provider haelt das Melde-Modal EINMAL fuer die ganze Anwendung und
 * bietet zwei Dinge an:
 *
 *   - {@link useTicketErstellen} — oeffnet das Modal. Die drei Detailseiten
 *     haengen damit einen Eintrag „Ticket erstellen" in ihr BESTEHENDES
 *     Menue; ihr Funktionsumfang bleibt unberuehrt.
 *   - {@link useEigenerFab} — meldet „hier gibt es schon einen FAB". Solange
 *     das der Fall ist, rendert der globale FAB sich NICHT. Damit steht auf
 *     keiner Seite mehr als ein Knopf, und auf jeder Seite mindestens einer.
 *
 * Bewusst ein Melde-Zaehler und kein Boolean: waehrend eines Seitenwechsels
 * sind der alte und der neue Bildschirm kurz gleichzeitig montiert. Ein
 * Boolean, das der scheidende beim Abmelden auf `false` setzt, loeschte die
 * Anmeldung des ankommenden gleich mit — der globale Knopf blitzte auf.
 */
interface FabAktionenWert {
  ticketErstellen: () => void;
  eigenenFabMelden: () => () => void;
}

const FabAktionenContext = createContext<FabAktionenWert | null>(null);

/**
 * Steht der globale FAB gerade im Bild? Eigener Kontext statt eines Felds in
 * `FabAktionenWert`: so rendert ein Wechsel nur die Schutzflaeche neu, nicht
 * jede Seite, die `useTicketErstellen`/`useEigenerFab` benutzt.
 */
const GlobalerFabSichtbarContext = createContext(false);

/**
 * Lage und Groesse des globalen FAB — eine Quelle fuer Knopf und Schutzflaeche.
 */
const FAB_ABSTAND = 20;
const FAB_GROESSE = 50;

/**
 * Hoehe der Schutzflaeche unter dem Seiteninhalt, solange der globale FAB
 * sichtbar ist: Abstand + Knopf + 10px Luft.
 *
 * Anlass (Feedback 13.10.2026): der schwebende „+"-Knopf lag auf /home/meetings
 * genau ueber dem rechten Pfeil der Paginierung — am Ende der Liste war die
 * naechste Seite nicht mehr klickbar. Der Knopf ist `position: fixed`; was
 * zuletzt im Scroll-Container steht, rutscht unter ihn. Die Flaeche gibt dem
 * Inhalt so viel Platz nach unten, dass sein Ende ueber dem Knopf endet —
 * auf JEDER Seite, nicht nur in der Meetings-Liste.
 */
export const FAB_SAFE_AREA = FAB_ABSTAND + FAB_GROESSE + 10;

/**
 * Leerer Platz am Ende des Shell-Scroll-Containers (`views/home/index.tsx`,
 * direkt hinter dem `<Outlet />`). Nur solange der GLOBALE FAB steht — eine
 * Seite mit eigenem FAB (Case, Beratung, Kundendetail) scrollt in ihrem
 * eigenen Container und behaelt ihr Layout unveraendert.
 *
 * `pointerEvents: 'none'` (Feedback 22.10.2026): eine Seite, die den Container
 * mit `flex: 1` fuellt (Telefonie-Wizard), wird um die Flaeche gestaucht, ihr
 * Inhalt ragt in sie hinein — und die durchsichtige Flaeche lag darueber und
 * fing die Klicks ab (Uhrzeit-Kacheln in Schritt 7 im Vollbild nicht klickbar).
 * Die Flaeche ist nur Abstand und darf nie Ziel eines Klicks sein.
 */
export const FabSchutzflaeche = () => {
  const sichtbar = useContext(GlobalerFabSichtbarContext);
  if (!sichtbar) return null;

  return (
    <Box
      aria-hidden
      data-testid="fab-schutzflaeche"
      sx={{ height: FAB_SAFE_AREA, flexShrink: 0, pointerEvents: 'none' }}
    />
  );
};

/**
 * Oeffnet das Melde-Modal. Ausserhalb des Providers ein No-op statt eines
 * Fehlers: eine Detailseite, die versehentlich ohne Rahmen gerendert wird
 * (etwa in einem Test), soll nicht daran zerbrechen.
 */
export const useTicketErstellen = (): (() => void) => {
  const wert = useContext(FabAktionenContext);
  return wert?.ticketErstellen ?? (() => {});
};

/** Meldet fuer die Lebensdauer der Komponente einen eigenen FAB an. */
export const useEigenerFab = (): void => {
  const wert = useContext(FabAktionenContext);
  const melden = wert?.eigenenFabMelden;

  useEffect(() => melden?.(), [melden]);
};

/**
 * Der Menueeintrag, den die drei Detailseiten in ihr eigenes FAB-Menue
 * haengen. Als Komponente und nicht als kopierter Block, damit Beschriftung
 * und Untertitel an genau einer Stelle stehen.
 */
export const TicketMenuEintrag = ({ onClick }: { onClick: () => void }) => (
  <MenuItem onClick={onClick} sx={{ py: 2 }}>
    <ListItemIcon sx={{ minWidth: 32 }}><Icon icon="Idea" size={20} sx={{ color: 'text.primary' }} /></ListItemIcon>
    <ListItemText>
      <Typography variant="wertBetont">Ticket erstellen</Typography>
      <Typography variant="klein">Fehler melden oder Wunsch äußern</Typography>
    </ListItemText>
  </MenuItem>
);

/**
 * Der globale FAB — sichtbar auf allen Seiten OHNE eigenen FAB. Optik und
 * Menue folgen dem Bestand aus `BeratungScrollView` (50px, secondary,
 * Dreh-Animation, zweizeilige Eintraege), damit sich beim Seitenwechsel nichts
 * aendert ausser dem Inhalt des Menues.
 */
const GlobalerFab = ({ onTicket }: { onTicket: () => void }) => {
  const [anker, setAnker] = useState<HTMLElement | null>(null);

  return (
    <>
      <Fab
        aria-label="Aktionen"
        onClick={(e) => setAnker(e.currentTarget)}
        sx={{
          position: 'fixed', bottom: FAB_ABSTAND, right: FAB_ABSTAND,
          bgcolor: 'secondary.main', color: 'secondary.contrastText',
          '&:hover': { bgcolor: 'secondary.dark' },
          zIndex: 20, width: FAB_GROESSE, height: FAB_GROESSE,
          transition: 'transform 0.2s',
          transform: anker ? 'rotate(45deg)' : 'none',
        }}
      >
        <Icon icon="Plus" size={22} sx={{ color: 'secondary.contrastText' }} />
      </Fab>
      <Menu
        anchorEl={anker}
        open={Boolean(anker)}
        onClose={() => setAnker(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { borderRadius: '8px', minWidth: 240, mb: 2 } } }}
      >
        <TicketMenuEintrag onClick={() => { setAnker(null); onTicket(); }} />
      </Menu>
    </>
  );
};

const FabAktionenProvider = ({ children }: { children: ReactNode }) => {
  const [modalOffen, setModalOffen] = useState(false);
  const [eigeneFabs, setEigeneFabs] = useState(0);

  const eigenenFabMelden = useCallback(() => {
    setEigeneFabs((n) => n + 1);
    return () => setEigeneFabs((n) => Math.max(0, n - 1));
  }, []);

  const wert = useMemo<FabAktionenWert>(
    () => ({ ticketErstellen: () => setModalOffen(true), eigenenFabMelden }),
    [eigenenFabMelden],
  );

  const globalerFabSichtbar = eigeneFabs === 0;

  return (
    <FabAktionenContext.Provider value={wert}>
      <GlobalerFabSichtbarContext.Provider value={globalerFabSichtbar}>
        {children}
      </GlobalerFabSichtbarContext.Provider>
      {globalerFabSichtbar && <GlobalerFab onTicket={() => setModalOffen(true)} />}
      {/* Das Modal liegt beim Provider, nicht beim Knopf: sonst gaebe es je
          Detailseite eine zweite Instanz mit eigenem Zustand. */}
      <FeedbackModal open={modalOffen} onClose={() => setModalOffen(false)} />
    </FabAktionenContext.Provider>
  );
};

export default FabAktionenProvider;
